import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { convertDayjsToUTC } from '@chiroup/core/functions/time';
import { portalSession, StoredPatientContext } from './portalSession';

type DejavooPaymentPayload = {
  dejavooPaymentTokenId: string;
  dejavooCardLastFour?: string;
  dejavooCardType?: string;
  dejavooCardholderName?: string;
};

type GuestSessionResponse = {
  appointmentId?: string;
  session?: {
    token: string;
    expiresAt: string;
    patient: StoredPatientContext;
  };
};

const appointmentService = () => {
  const create = async (
    body: any,
    paymentKey?: string,
    dejavooPayment?: DejavooPaymentPayload
  ): Promise<any> => {
    return ChiroUpAPI.post('api', `/appointment`, {
      body: {
        ...body,
        startTime: convertDayjsToUTC({ datetime: body.startTime }),
        endTime: convertDayjsToUTC({ datetime: body.endTime }),
        paymentKey,
        ...(dejavooPayment || {}),
        doubleBook: body.doubleBook,
      },
    });
  };

  /**
   * Public booking for a new patient who has verified their email or
   * phone via OTP but doesn't have a Patient row yet. The backend
   * creates the pending Patient + appointment and issues a portal
   * session in one shot; on success we persist the returned token /
   * patient so the caller can refresh `me` and render the post-book
   * confirmation as an authenticated user.
   */
  const createGuest = async (
    body: {
      challengeId: string;
      path: string;
      fname: string;
      lname: string;
      dateOfBirth: string;
      treatment: number;
      provider: string;
      date?: string;
      startTime: any;
      endTime: any;
      email?: string | null;
      phone?: string | null;
      language?: 'en' | 'es';
    },
    paymentKey?: string,
    dejavooPayment?: DejavooPaymentPayload
  ): Promise<GuestSessionResponse> => {
    const res = (await ChiroUpAPI.post('api-noauth', `/appointment-guest`, {
      body: {
        ...body,
        startTime: convertDayjsToUTC({ datetime: body.startTime }),
        endTime: convertDayjsToUTC({ datetime: body.endTime }),
        paymentKey,
        ...(dejavooPayment || {}),
      },
    })) as GuestSessionResponse;

    if (res?.session?.token) {
      portalSession.setToken(res.session.token, res.session.expiresAt);
    }

    if (res?.session?.patient) {
      portalSession.setPatient(res.session.patient);
    }

    return res;
  };

  const update = async (id: string, body: any): Promise<any> => {
    return ChiroUpAPI.put('api', `/appointment/${id}`, {
      body: {
        ...body,
        startTime: convertDayjsToUTC({ datetime: body.startTime }),
        endTime: convertDayjsToUTC({ datetime: body.endTime }),
      },
    });
  };

  return {
    create,
    createGuest,
    update,
  };
};

export default appointmentService();
